import React, {Dispatch, SetStateAction} from 'react'
import {Button, Icon, Select, Text} from '@therms/atalaya'
import {
  LabeledInput,
  RecordTagTitles,
  RPCDocs,
  SelectRecordTag,
  toOptions,
  useRecordsFieldOptionsConfig,
  useUrlState,
} from '@therms/react-modules'
import {
  ItemSearch,
  RecordSearchState,
} from '@src/modules/Records/shared/RecordsList'
import {IoClose} from 'react-icons/io5'
import {MdExpandLess, MdExpandMore} from 'react-icons/md'

interface RecordItemSearchFiltersProps {
  filtersState: ItemSearch | undefined
  searchText: string
  setFiltersState: Dispatch<SetStateAction<RecordSearchState>>
  setSearchText: Dispatch<SetStateAction<string>>
}

function RecordItemSearchFilters({
  filtersState,
  searchText,
  setFiltersState,
  setSearchText,
}: RecordItemSearchFiltersProps) {
  const [showAdvanceFilters, setAdvanceFilters] = useUrlState(
    'recorditemfilters',
    false,
  )
  const config = useRecordsFieldOptionsConfig()

  return (
    <div className="flex w-full flex-col">
      <div className="pb-xxs gap-x-xxs inline-flex">
        <Text
          placeholder="Search..."
          onChangeValue={setSearchText}
          value={searchText}
          search
        />

        <Button
          onClick={() => setAdvanceFilters(!showAdvanceFilters)}
          size="base"
          subtle
          variant="neutral"
        >
          <span>Filters</span>
          {showAdvanceFilters ? (
            <Icon Icon={MdExpandLess} />
          ) : (
            <Icon Icon={MdExpandMore} />
          )}
        </Button>

        {(filtersState?.values?.color ||
          filtersState?.values?.status ||
          !!filtersState?.values?.tags?.length ||
          !!searchText) && (
          <Button
            onClick={() => {
              setAdvanceFilters(false)
              setFiltersState({
                item: {
                  values: {color: undefined, status: undefined, tags: []},
                },
              })
              setSearchText('')
            }}
            subtle
          >
            <Icon color="critical" Icon={IoClose} /> Clear
          </Button>
        )}
      </div>

      {showAdvanceFilters && (
        <div className="gap-xxs grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
          <div className="col-span-2 md:col-span-2">
            <LabeledInput
              label="Record tags"
              nameElement={
                <RecordTagTitles
                  recordTagIds={filtersState?.values?.tags}
                  listAll={false}
                />
              }
              inputElement={
                <SelectRecordTag
                  typeFilter={[
                    RPCDocs.Records.GetRecordTagsList.v1.TypeFilter.Item,
                  ]}
                  isClearable
                  combineSelected
                  menuIsOpen
                  multi
                  optionLabelExtractor={(option) => option.title}
                  optionValueExtractor={(option) => option.id}
                  onChangeValue={(val) =>
                    setFiltersState({
                      item: {values: {tags: val}},
                    })
                  }
                  placeholder="Record Tags"
                  value={filtersState?.values?.tags}
                />
              }
              value={filtersState?.values?.tags}
            />
          </div>

          <div className="col-span-2 md:col-span-2">
            <LabeledInput
              label="Status"
              nameElement={<div>{filtersState?.values?.status}</div>}
              inputElement={
                <Select
                  allowCreate
                  isClearable
                  menuIsOpen
                  options={toOptions(config?.item.status)}
                  onChangeValue={(val) => {
                    setFiltersState({item: {values: {status: val}}})
                  }}
                  placeholder="Status"
                  value={filtersState?.values?.status}
                />
              }
              value={filtersState?.values?.status}
            />
          </div>

          <div className="col-span-2 md:col-span-2">
            <LabeledInput
              label="Colors"
              nameElement={<div>{filtersState?.values?.color}</div>}
              inputElement={
                <Select
                  allowCreate
                  isClearable
                  menuIsOpen
                  options={toOptions(config?.item.color)}
                  onChangeValue={(val) => {
                    setFiltersState({item: {values: {color: val}}})
                  }}
                  placeholder="Colors"
                  value={filtersState?.values?.color}
                />
              }
              value={filtersState?.values?.color}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export {RecordItemSearchFilters}